import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faLocationDot,
  faScrewdriverWrench,
  faClipboardCheck,
  faFileInvoiceDollar,
  faHourglassHalf,
  faClockRotateLeft,
  faCircleCheck,
  faClock,
  faBuilding,
  faChevronRight,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { customerDashboardService } from '@/services/customer-dashboard.service';
import { customerJobService } from '@/services/customer-job.service';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { locationParamFor, useCustomerScopeStore } from '@/stores/customer-scope.store';
import { CustomerLocationContext } from '@/components/customer-portal/CustomerLocationContext';
import { CustomerDecisionCard } from '../decisions/CustomerDecisionCard';
import { mostUrgent, nextUp } from '../decisions/customer-decision-queue';
import { CUSTOMER_PORTAL_ROUTES, customerJobDetailPath } from '@/routes/customer-portal-routes';
import { useCustomerLiveClock } from '@/hooks/use-customer-live-clock';
import { useCustomerSessionDuration } from '@/hooks/use-customer-session-duration';
import { CustomerSectionCard } from '@/components/customer-portal/CustomerSectionCard';
import { CustomerStatCard } from '@/components/customer-portal/CustomerStatCard';
import { CustomerEmptyState } from '@/components/customer-portal/CustomerEmptyState';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { CustomerStatusBadge } from '@/components/customer-portal/CustomerStatusBadge';
import type { CustomerBadgeTone } from '@/components/customer-portal/CustomerStatusBadge';
import { CustomerSkeletonDashboard } from '@/components/customer-portal/skeletons/CustomerSkeleton';

const STATUS_LABELS: Record<string, string> = {
  UNASSIGNED: 'Unassigned',
  SCHEDULED: 'Scheduled',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  REVIEWED: 'Reviewed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
  TRANSFER_REQUESTED: 'Transfer Requested',
};

const STATUS_TONES: Record<string, CustomerBadgeTone> = {
  UNASSIGNED: 'neutral',
  SCHEDULED: 'info',
  ASSIGNED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  REVIEWED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'danger',
  TRANSFER_REQUESTED: 'warning',
};

function formatRelativeDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** How many "next up" decisions the dashboard shows. The dashboard must stay
 * compact with 100+ pending decisions — the rest live in the decision queue. */
const NEXT_UP_LIMIT = 4;
/** One page is all the dashboard ever asks for: enough to pick the most urgent
 * item and a short next-up list, never the whole queue. */
const DECISION_PREVIEW_SIZE = 10;

export function CustomerDashboardPage() {
  const user = useCustomerAuthStore((s) => s.user);
  const navigate = useNavigate();
  const clock = useCustomerLiveClock();
  const sessionDuration = useCustomerSessionDuration();
  const selectedLocation = useCustomerScopeStore((s) => s.selectedLocationId);
  // D-130 — the same server-validated parameter every list uses, so the
  // dashboard's counts and the lists they link to always agree.
  const locationId = locationParamFor(user?.locationScope, selectedLocation);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['customer-portal', 'dashboard', locationId ?? 'all'],
    queryFn: () => customerDashboardService.getDashboard({ locationId }),
  });

  // A single short page of the decisions the server says are waiting. This is a
  // PREVIEW of the queue, not the queue: at most DECISION_PREVIEW_SIZE rows are
  // ever fetched, so 100+ pending decisions cost the dashboard nothing extra.
  const { data: decisions } = useQuery({
    queryKey: ['customer-portal', 'decisions', 'preview', locationId ?? 'all'],
    queryFn: () =>
      customerJobService.list({
        page: 1,
        pageSize: DECISION_PREVIEW_SIZE,
        actionRequired: true,
        sortBy: 'updatedAt',
        sortDir: 'asc',
        locationId,
      }),
  });

  if (isLoading) return <CustomerSkeletonDashboard />;

  if (isError || !data) {
    return (
      <CustomerErrorState
        message="We couldn't load your dashboard."
        onRetry={() => void refetch()}
      />
    );
  }

  const attention = data.pendingDecisionsCount + data.pendingQuotationsCount;
  const hasAttentionItems = attention > 0;
  // Presentation-only derivations over the page the server already returned.
  const decisionItems = decisions?.items ?? [];
  const urgent = mostUrgent(decisionItems);
  const upNext = nextUp(decisionItems, NEXT_UP_LIMIT);
  const shown = (urgent ? 1 : 0) + upNext.length;
  const remaining = Math.max(0, (decisions?.total ?? 0) - shown);
  const customerName = data.customer.name ?? user?.customerName ?? 'your account';

  const summary = hasAttentionItems
    ? `${attention} item${attention === 1 ? '' : 's'} need${attention === 1 ? 's' : ''} your attention.`
    : data.activeInspectionsCount > 0
      ? `${data.activeInspectionsCount} active job${data.activeInspectionsCount === 1 ? '' : 's'} in progress. Nothing needs your decision right now.`
      : 'No active service activity right now — you’re all caught up.';

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* ── Greeting ────────────────────────────────────────────────── */}
      <div className="customer-fade-in rounded-customer-lg border border-customer-border bg-customer-surface p-4 shadow-customer-sm sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="flex min-w-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-customer-primary-text">
                <FontAwesomeIcon icon={faBuilding} className="h-3 w-3" aria-hidden="true" />
                <span className="truncate">{customerName}</span>
              </p>
              {/* D-130 — the location this view is scoped to, at every width. */}
              <CustomerLocationContext />
            </div>
            <h1 className="mt-1.5 text-2xl font-semibold leading-tight tracking-tight text-customer-text sm:text-3xl">
              {clock.greeting}
              {user ? `, ${user.firstName}` : ''}
            </h1>
            <p
              className={`mt-2 max-w-xl text-sm leading-5 ${
                hasAttentionItems
                  ? 'font-medium text-customer-text'
                  : 'text-customer-text-secondary'
              }`}
            >
              {summary}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3 text-xs text-customer-text-secondary sm:flex-col sm:items-end sm:gap-1 sm:text-right">
            <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums text-customer-text">
              <FontAwesomeIcon
                icon={faClock}
                className="h-3.5 w-3.5 text-customer-text-secondary"
                aria-hidden="true"
              />
              {clock.time}
            </span>
            <span>{clock.date}</span>
            <span
              className="hidden text-[11px] text-customer-text-secondary sm:block"
              title="Time elapsed since this portal session was opened"
            >
              Session {sessionDuration}
            </span>
          </div>
        </div>
      </div>

      {/* ── Waiting on you ──────────────────────────────────────────────
          Compact by design: a count, the single most urgent item, a short
          next-up list and one link to the full queue. It never renders one
          card per pending decision — with 100+ waiting that would bury the
          rest of the dashboard and make the page unusable on a phone. The
          preview asks the server for a single short page (see
          DECISION_PREVIEW_SIZE); the authoritative list lives at
          CUSTOMER_PORTAL_ROUTES.DECISIONS. */}
      <CustomerSectionCard
        title="Waiting on you"
        icon={faTriangleExclamation}
        tone={hasAttentionItems ? 'warning' : 'default'}
        description={
          hasAttentionItems
            ? `${attention} item${attention === 1 ? '' : 's'} need${attention === 1 ? 's' : ''} a decision from you.`
            : 'Nothing is waiting on you.'
        }
        actions={
          hasAttentionItems ? (
            /* 6R: `size="sm"` relaxes to 40px from `sm:` upwards. That is fine
               for a minor control, but this is the dashboard's primary route
               into the decision queue, so it keeps the full 44px at every
               width. The shared `sm` size is unchanged. */
            <CustomerButton
              variant="secondary"
              icon={faArrowRight}
              onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.DECISIONS)}
            >
              View all decisions
            </CustomerButton>
          ) : undefined
        }
        flush={hasAttentionItems && Boolean(urgent)}
      >
        {!hasAttentionItems ? (
          <CustomerEmptyState
            icon={faCircleCheck}
            title="You're all caught up"
            description="Nothing requires your attention right now."
            className="py-5"
          />
        ) : urgent ? (
          <div>
            <p className="px-4 pt-3 text-[11px] font-semibold uppercase tracking-wide text-customer-text-secondary sm:px-5">
              Waiting longest
            </p>
            <CustomerDecisionCard
              item={urgent}
              emphasis
              onOpen={(id) => navigate(customerJobDetailPath(id))}
            />
            {upNext.length > 0 && (
              <>
                <p className="border-t border-customer-border px-4 pt-3 text-[11px] font-semibold uppercase tracking-wide text-customer-text-secondary sm:px-5">
                  Next up
                </p>
                <ul className="divide-y divide-customer-border">
                  {upNext.map((item) => (
                    <li key={item.id}>
                      <CustomerDecisionCard
                        item={item}
                        onOpen={(id) => navigate(customerJobDetailPath(id))}
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
            {remaining > 0 && (
              <div className="border-t border-customer-border px-4 py-3 sm:px-5">
                <button
                  type="button"
                  onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.DECISIONS)}
                  className="customer-pressable min-h-11 text-sm font-semibold text-customer-action-text underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  {remaining} more waiting — view all decisions
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Counts say work is waiting but the preview has not arrived (or the
             two disagree). Never guess: point at the queue, which is the
             authoritative, server-scoped list. */
          <CustomerButton
            variant="secondary"
            icon={faArrowRight}
            onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.DECISIONS)}
          >
            View all decisions
          </CustomerButton>
        )}
      </CustomerSectionCard>

      {/* ── Service Overview ────────────────────────────────────────── */}
      <div>
        <h2 className="mb-2.5 text-[15px] font-semibold text-customer-text">Service Overview</h2>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <CustomerStatCard
            label="Locations"
            value={data.locationCount}
            icon={faLocationDot}
            hint="Registered sites"
            index={0}
          />
          <CustomerStatCard
            label="Equipment"
            value={data.equipmentCount}
            icon={faScrewdriverWrench}
            to={CUSTOMER_PORTAL_ROUTES.EQUIPMENT}
            hint="View equipment"
            index={1}
          />
          <CustomerStatCard
            label="Active Jobs"
            value={data.activeInspectionsCount}
            icon={faClipboardCheck}
            to={CUSTOMER_PORTAL_ROUTES.JOBS}
            hint="View jobs"
            index={2}
          />
          <CustomerStatCard
            label="Pending Quotations"
            value={data.pendingQuotationsCount}
            icon={faFileInvoiceDollar}
            to={CUSTOMER_PORTAL_ROUTES.DECISIONS}
            hint={data.pendingQuotationsCount > 0 ? 'Needs review' : 'None pending'}
            index={3}
          />
        </div>
      </div>

      {/* ── Recent Activity ─────────────────────────────────────────── */}
      <CustomerSectionCard title="Recent Activity" icon={faClockRotateLeft} flush>
        {data.recentActivity.length === 0 ? (
          <CustomerEmptyState
            icon={faHourglassHalf}
            tone="neutral"
            title="No inspections yet"
            description="Service activity for your equipment will appear here."
          />
        ) : (
          <ul className="divide-y divide-customer-border">
            {data.recentActivity.map((item) => {
              const tone = STATUS_TONES[item.status] ?? 'neutral';
              return (
                <li key={item.inspectionId}>
                  <button
                    onClick={() => navigate(customerJobDetailPath(item.inspectionId))}
                    className="customer-pressable flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-customer-ring sm:px-5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-customer-text">
                        {item.inspectionNumber}
                      </p>
                      <p className="mt-0.5 text-xs text-customer-text-secondary">
                        {formatRelativeDate(item.updatedAt)}
                      </p>
                    </div>
                    <CustomerStatusBadge
                      label={STATUS_LABELS[item.status] ?? item.status}
                      tone={tone}
                    />
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="h-3 w-3 shrink-0 text-customer-text-secondary"
                      aria-hidden="true"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CustomerSectionCard>

      {/* ── Quick Actions ────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <CustomerButton
          variant="outline"
          icon={faClipboardCheck}
          onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.JOBS)}
        >
          View Jobs
        </CustomerButton>
        <CustomerButton
          variant="outline"
          icon={faScrewdriverWrench}
          onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.EQUIPMENT)}
        >
          View Equipment
        </CustomerButton>
        {attention > 0 && (
          <CustomerButton
            variant="secondary"
            icon={faTriangleExclamation}
            onClick={() => navigate(CUSTOMER_PORTAL_ROUTES.DECISIONS)}
          >
            Review decisions
          </CustomerButton>
        )}
      </div>
    </div>
  );
}
